import {
	Button,
	ButtonGroup,
	Modal,
	OverlayTrigger,
	Popover,
	Table,
	ToggleButton,
} from "react-bootstrap";
import Select from "react-select";
import type { ReactSelectOption } from "../types/assignment.types";
import {
	type DispatchRule,
	type DispatchRuleType,
	type NotificationMethod,
	notificationMethodMap,
	type ProfitCenter,
	type TicketNotificationConfig,
} from "../types/rules.types";
import "../styles/Rules.css";

type RulesListProps = {
	rulesData: DispatchRule[];
	getRulesData: () => void;
	showInactiveRules: boolean;
	updateShowInactiveRules: (show: boolean) => void;
	showCreateRuleModal: boolean;
	updateShowCreateRuleModal: (show: boolean) => void;
	profitCenters: ProfitCenter[];
	selectedProfitCenterId: string | undefined;
	updateSelectedProfitCenterId: (id: string) => void;
	clearSelectedProfitCenterId: () => void;
	checkedDefaultNotificationType: NotificationMethod | undefined;
	updateCheckedDefaultNotificationType: (method: NotificationMethod) => void;
	clearCheckedDefaultNotificationType: () => void;
	checkedUpdateNotificationType: NotificationMethod | undefined;
	updateCheckedUpdateNotificationType: (method: NotificationMethod) => void;
	clearCheckedUpdateNotificationType: () => void;
	checkedCancelNotificationType: NotificationMethod | undefined;
	updateCheckedCancelNotificationType: (method: NotificationMethod) => void;
	clearCheckedCancelNotificationType: () => void;
	checkedRecallNotificationType: NotificationMethod | undefined;
	updateCheckedRecallNotificationType: (method: NotificationMethod) => void;
	clearCheckedRecallNotificationType: () => void;
	TicketNotificationConfigs: TicketNotificationConfig[];
	ruleTypes: DispatchRuleType[];
	selectedRuleTypeId: string | undefined;
	updateSelectedRuleTypeId: (id: string) => void;
	createRule: () => void;
	selectedStartTime: string | undefined;
	selectedEndTime: string | undefined;
	updateSelectedStartTime: (time: string) => void;
	updateSelectedEndTime: (time: string) => void;
	clearSelectedRuleTypeId: () => void;
	clearSelectedStartTime: () => void;
	clearSelectedEndTime: () => void;
	canCreateRule: boolean;
	handleRuleRowClick: (rule: DispatchRule) => void;
	showEditRuleModal: boolean;
	updateShowEditRuleModal: (show: boolean) => void;
	EditTicketNotificationConfigs: TicketNotificationConfig[];
	editRuleTypeId: string | undefined;
	updateEditRuleTypeId: (id: string) => void;
	editProfitCenterId: string | undefined;
	updateEditProfitCenterId: (id: string) => void;
	editStartTime: string | undefined;
	updateEditStartTime: (time: string) => void;
	editEndTime: string | undefined;
	updateEditEndTime: (time: string) => void;
	canEditRule: boolean;
	editRule: () => void;
	canDeleteRule: boolean;
	deleteRule: () => void;
};

export default function RulesList(props: RulesListProps) {
	const {
		rulesData,
		showInactiveRules,
		updateShowInactiveRules,
		showCreateRuleModal,
		updateShowCreateRuleModal,
		profitCenters,
		selectedProfitCenterId,
		updateSelectedProfitCenterId,
		TicketNotificationConfigs,
		ruleTypes,
		selectedRuleTypeId,
		updateSelectedRuleTypeId,
		createRule,
		selectedStartTime,
		selectedEndTime,
		updateSelectedStartTime,
		updateSelectedEndTime,
		canCreateRule,
		handleRuleRowClick,
		showEditRuleModal,
		updateShowEditRuleModal,
		EditTicketNotificationConfigs,
		editRuleTypeId,
		updateEditRuleTypeId,
		editProfitCenterId,
		updateEditProfitCenterId,
		editStartTime,
		updateEditStartTime,
		editEndTime,
		updateEditEndTime,
		canEditRule,
		editRule,
		canDeleteRule,
		deleteRule,
	} = props;

	const handleShowInactiveClick = () => {
		updateShowInactiveRules(!showInactiveRules);
	};

	const handleShowCreateRuleModalClick = () => {
		updateShowCreateRuleModal(true);
	};

	const formatSelectedProfitCenter = (): ReactSelectOption | null => {
		const id = selectedProfitCenterId ? Number(selectedProfitCenterId) : null;
		if (!id) return null;

		const profitCenter = profitCenters.find((pc) => pc.ProfitCenterID === id);
		if (!profitCenter) return null;

		return {
			value: profitCenter.ProfitCenterID.toString(),
			label: profitCenter.ProfitCenterLongDesc,
		};
	};

	const formatSelectedRuleType = (): ReactSelectOption | null => {
		const id = selectedRuleTypeId ? Number(selectedRuleTypeId) : null;
		if (!id) return null;

		const ruleType = ruleTypes.find((rt) => rt.rule_type_id === id);
		if (!ruleType) return null;

		return {
			value: ruleType.rule_type_id.toString(),
			label: ruleType.rule_type_name,
		};
	};

	const formatEditRuleType = (): ReactSelectOption | null => {
		const id = editRuleTypeId ? Number(editRuleTypeId) : null;
		if (!id) return null;

		const ruleType = ruleTypes.find((rt) => rt.rule_type_id === id);
		if (!ruleType) return null;

		return {
			value: ruleType.rule_type_id.toString(),
			label: ruleType.rule_type_name,
		};
	};

	const formatEditProfitCenter = (): ReactSelectOption | null => {
		const id = editProfitCenterId ? Number(editProfitCenterId) : null;
		if (!id) return null;

		const profitCenter = profitCenters.find((pc) => pc.ProfitCenterID === id);
		if (!profitCenter) return null;

		return {
			value: profitCenter.ProfitCenterID.toString(),
			label: profitCenter.ProfitCenterLongDesc,
		};
	};

	const handleCreateRuleClick = () => {
		createRule();
	};

	const compactStyles = {
		control: (base: any) => ({
			...base,
			minHeight: 32,
			height: 32,
		}),
		valueContainer: (base: any) => ({
			...base,
			padding: "0 8px",
		}),
		input: (base: any) => ({
			...base,
			margin: 0,
			padding: 0,
		}),
		indicatorsContainer: (base: any) => ({
			...base,
			height: 32,
		}),
		dropdownIndicator: (base: any) => ({
			...base,
			padding: 4,
		}),
		clearIndicator: (base: any) => ({
			...base,
			padding: 4,
		}),
		option: (base: any) => ({
			...base,
			padding: "6px 10px",
		}),
		menuList: (base: any) => ({
			...base,
			maxHeight: 90,
		}),
	};

	return (
		<div className="dispatchRulesListContainer">
			<div className="dispatchRulesListButtonWrapper">
				<button
					type="button"
					className="dispatchRulesListCreateButtonWrapper"
					onClick={() => handleShowCreateRuleModalClick()}
				>
					<div className="dispatchRulesListCreateButtonIcon">+</div>
				</button>
				<button
					type="button"
					className="dispatchRulesShowInactiveButtonContainer"
					onClick={() => handleShowInactiveClick()}
				>
					<div className="dispatchRulesShowInactiveButtonWrapper">
						<i className="material-icons">
							{showInactiveRules ? "check_box" : "check_box_outline_blank"}
						</i>
						<span>Show Inactive</span>
					</div>
				</button>
			</div>
			<Modal
				size="lg"
				show={showCreateRuleModal}
				onHide={() => updateShowCreateRuleModal(false)}
				backdrop="static"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Create Rule</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="dispatchRulesCreateRuleFormSection">
						<div className="dispatchRulesCreateRuleFormItem">
							<div>Rule Type</div>
							<Select
								options={ruleTypes.map((ruleType: DispatchRuleType) => ({
									value: ruleType.rule_type_id,
									label: ruleType.rule_type_name,
								}))}
								value={formatSelectedRuleType()}
								onChange={(selected: ReactSelectOption) => {
									updateSelectedRuleTypeId(selected.value);
								}}
								styles={compactStyles}
							/>
						</div>
						<div className="dispatchRulesCreateRuleFormItem">
							<div>Profit Center</div>
							<Select
								options={profitCenters.map((profitCenter) => ({
									value: profitCenter.ProfitCenterID,
									label: profitCenter.ProfitCenterLongDesc,
								}))}
								value={formatSelectedProfitCenter()}
								onChange={(selected: ReactSelectOption) => {
									updateSelectedProfitCenterId(selected.value);
								}}
								styles={compactStyles}
							/>
						</div>
					</div>
					<div className="dispatchRulesCreateRuleFormSection">
						<div className="dispatchRulesCreateRuleFormItem">
							<div>Start Time</div>
							<input
								className="dispatchRulesCreateRuleTimeFormItem"
								type="time"
								value={selectedStartTime}
								onChange={(e) => updateSelectedStartTime(e.target.value)}
							/>
						</div>
						<div className="dispatchRulesCreateRuleFormItem">
							<div>End Time</div>
							<input
								className="dispatchRulesCreateRuleTimeFormItem"
								type="time"
								value={selectedEndTime}
								onChange={(e) => updateSelectedEndTime(e.target.value)}
							/>
						</div>
					</div>
					<div className="dispatchRulesCreateRuleFormNotificationTypeContainer">
						{TicketNotificationConfigs.map((x, idx) => (
							<div
								key={`${x.label}-${idx}-container`}
								className="dispatchRulesCreateRuleFormNotificationTypeWrapper"
							>
								<div
									key={`${x.label}-${idx}-label-wrapper`}
									className="dispatchRulesCreateRuleFormNotificationTypeLabelContainer"
								>
									<div
										key={`${x.label}-${idx}-label`}
										className="dispatchRulesCreateRuleFormNotificationTypeLabelContainer"
									>
										{x.label} Notification Type
									</div>
									{x.label !== "Default" && (
										<div key={`${x.label}-${idx}-info-icon-wrapper`}>
											<OverlayTrigger
												trigger={["hover", "focus"]}
												key={`overlay-${x.label}`}
												placement="right"
												overlay={
													<Popover
														key={`${x.label}-${idx}-popover`}
														id={`popover-${x.label}`}
													>
														<Popover.Content
															key={`${x.label}-${idx}-popover-content`}
														>
															Ticket type contains '{x.label}'.
														</Popover.Content>
													</Popover>
												}
											>
												<i
													key={`${x.label}-${idx}-icon`}
													className="material-icons dispatchRulesCreateRuleFormNotificationTypeLabelInfoIcon"
												>
													info
												</i>
											</OverlayTrigger>
										</div>
									)}
								</div>
								<ButtonGroup toggle key={`${x.label}-${idx}-button-group`}>
									{Object.entries(notificationMethodMap).map(
										([key, value], innerIdx) => (
											<ToggleButton
												type="radio"
												checked={x.value === Number(key)}
												onChange={(e) =>
													x.UpdateValue(
														Number(e.target.value) as NotificationMethod,
													)
												}
												variant="secondary"
												name={`notification-${x.label}`}
												value={key}
												key={`${x.label}-${idx}-${innerIdx}-${value}`}
											>
												{value}
											</ToggleButton>
										),
									)}
								</ButtonGroup>
							</div>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						disabled={!canCreateRule}
						onClick={() => handleCreateRuleClick()}
					>
						Save
					</Button>
					<Button
						variant="secondary"
						onClick={() => updateShowCreateRuleModal(false)}
					>
						Cancel
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				size="lg"
				show={showEditRuleModal}
				onHide={() => updateShowEditRuleModal(false)}
				backdrop="static"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Edit Rule</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="dispatchRulesCreateRuleFormSection">
						<div className="dispatchRulesCreateRuleFormItem">
							<div>Rule Type</div>
							<Select
								options={ruleTypes.map((ruleType: DispatchRuleType) => ({
									value: ruleType.rule_type_id,
									label: ruleType.rule_type_name,
								}))}
								value={formatEditRuleType()}
								onChange={(selected: ReactSelectOption) => {
									updateEditRuleTypeId(selected.value);
								}}
								styles={compactStyles}
							/>
						</div>
						<div className="dispatchRulesCreateRuleFormItem">
							<div>Profit Center</div>
							<Select
								options={[formatEditProfitCenter()]}
								value={formatEditProfitCenter()}
								isDisabled={true}
								styles={compactStyles}
							/>
						</div>
					</div>
					<div className="dispatchRulesCreateRuleFormSection">
						<div className="dispatchRulesCreateRuleFormItem">
							<div>Start Time</div>
							<input
								className="dispatchRulesCreateRuleTimeFormItem"
								type="time"
								value={editStartTime}
								onChange={(e) => updateEditStartTime(e.target.value)}
							/>
						</div>
						<div className="dispatchRulesCreateRuleFormItem">
							<div>End Time</div>
							<input
								className="dispatchRulesCreateRuleTimeFormItem"
								type="time"
								value={editEndTime}
								onChange={(e) => updateEditEndTime(e.target.value)}
							/>
						</div>
					</div>
					<div className="dispatchRulesCreateRuleFormNotificationTypeContainer">
						{EditTicketNotificationConfigs.map((x, idx) => (
							<div
								key={`edit-${x.label}-${idx}-container`}
								className="dispatchRulesCreateRuleFormNotificationTypeWrapper"
							>
								<div
									key={`edit-${x.label}-${idx}-label-wrapper`}
									className="dispatchRulesCreateRuleFormNotificationTypeLabelContainer"
								>
									<div
										key={`edit-${x.label}-${idx}-label`}
										className="dispatchRulesCreateRuleFormNotificationTypeLabelContainer"
									>
										{x.label} Notification Type
									</div>
									{x.label !== "Default" && (
										<div key={`edit-${x.label}-${idx}-info-icon-wrapper`}>
											<OverlayTrigger
												trigger={["hover", "focus"]}
												key={`edit-overlay-${x.label}`}
												placement="right"
												overlay={
													<Popover
														key={`edit-${x.label}-${idx}-popover`}
														id={`edit-popover-${x.label}`}
													>
														<Popover.Content
															key={`edit-${x.label}-${idx}-popover-content`}
														>
															Ticket type contains '{x.label}'.
														</Popover.Content>
													</Popover>
												}
											>
												<i
													key={`edit-${x.label}-${idx}-icon`}
													className="material-icons dispatchRulesCreateRuleFormNotificationTypeLabelInfoIcon"
												>
													info
												</i>
											</OverlayTrigger>
										</div>
									)}
								</div>
								<ButtonGroup toggle key={`edit-${x.label}-${idx}-button-group`}>
									{Object.entries(notificationMethodMap).map(
										([key, value], innerIdx) => (
											<ToggleButton
												type="radio"
												checked={x.value === Number(key)}
												onChange={(e) =>
													x.UpdateValue(
														Number(e.target.value) as NotificationMethod,
													)
												}
												variant="secondary"
												name={`edit-notification-${x.label}`}
												value={key}
												key={`edit-${x.label}-${idx}-${innerIdx}-${value}`}
											>
												{value}
											</ToggleButton>
										),
									)}
								</ButtonGroup>
							</div>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button disabled={!canEditRule} onClick={() => editRule()}>
						Save
					</Button>
					<Button
						variant="danger"
						disabled={!canDeleteRule}
						onClick={() => deleteRule()}
					>
						Delete
					</Button>
					<Button
						variant="secondary"
						onClick={() => updateShowEditRuleModal(false)}
					>
						Cancel
					</Button>
				</Modal.Footer>
			</Modal>
			{rulesData.length > 0 && (
				<div className="dispatchRulesListTableContainer">
					<Table striped bordered hover>
						<thead>
							<tr>
								<th>Rule</th>
								<th>Start Time</th>
								<th>End Time</th>
								<th>Profit Center</th>
								<th>Default Notification</th>
								<th>Update Notification Type</th>
								<th>Cancel Notification Type</th>
								<th>Recall Notification Type</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{rulesData.map((rule, _idx) => {
								if (!showInactiveRules && rule.deleted_date) {
									return null;
								}

								return (
									<tr
										key={rule.profit_center_dispatch_rule_id}
										onClick={() => handleRuleRowClick(rule)}
									>
										<td key={`col-rule-${rule.profit_center_dispatch_rule_id}`}>
											{rule.rule_type?.rule_type_name}
										</td>
										<td
											key={`col-start-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.rule_start_time}
										</td>
										<td key={`col-end-${rule.profit_center_dispatch_rule_id}`}>
											{rule.rule_end_time}
										</td>
										<td
											key={`col-profit-center-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.profit_center_name}
										</td>
										<td
											key={`col-default-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.notification_type
												? notificationMethodMap[
														rule.notification_type as NotificationMethod
													]
												: ""}
										</td>
										<td
											key={`col-update-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.update_ticket_notification_type
												? notificationMethodMap[
														rule.update_ticket_notification_type as NotificationMethod
													]
												: ""}
										</td>
										<td
											key={`col-cancel-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.cancel_ticket_notification_type
												? notificationMethodMap[
														rule.cancel_ticket_notification_type as NotificationMethod
													]
												: ""}
										</td>
										<td
											key={`col-recall-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.recall_ticket_notification_type
												? notificationMethodMap[
														rule.recall_ticket_notification_type as NotificationMethod
													]
												: ""}
										</td>
										<td
											key={`col-status-${rule.profit_center_dispatch_rule_id}`}
										>
											{rule.deleted_date != null ? "Inactive" : "Active"}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			)}
		</div>
	);
}
